import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Package, BookOpen, Upload, Database, Users, LogOut, ChevronRight, LayoutDashboard, Key, Settings, Building2 } from 'lucide-react';
import ChangePasswordModal from '../components/ChangePasswordModal';
import '@/App.css';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/products', icon: Package, label: 'Products' },
  { to: '/bulk-upload', icon: Upload, label: 'Bulk Upload' },
  { to: '/catalogs', icon: BookOpen, label: 'Catalogs' },
  { to: '/corporate-catalogs', icon: Building2, label: 'Corporate Catalogs' },
  { to: '/taxonomy', icon: Database, label: 'Master Data' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-[260px] bg-[#F5F2EB] border-r border-[#E5E0D8] flex flex-col z-50" data-testid="sidebar">
        <div className="p-6 border-b border-[#E5E0D8]">
          <img src="/logo-white.png" alt="AARYA24kt" className="h-10 mb-1" />
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5C5C] font-sans">Catalog System</p>
        </div>

        <nav className="flex-1 py-4" data-testid="sidebar-nav">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}>
              <item.icon size={18} />
              <span>{item.label}</span>
              <ChevronRight size={14} className="ml-auto opacity-0 group-hover:opacity-100" />
            </NavLink>
          ))}
          {user?.role === 'super_admin' && (
            <NavLink to="/users" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} data-testid="nav-users">
              <Users size={18} />
              <span>Users</span>
            </NavLink>
          )}
          {user?.role === 'super_admin' && (
            <NavLink to="/settings" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} data-testid="nav-settings">
              <Settings size={18} />
              <span>Settings</span>
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-[#E5E0D8]">
          <div className="mb-3">
            <p className="text-sm font-medium text-[#1A1A1A]">{user?.name}</p>
            <p className="text-xs text-[#5C5C5C] capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="change-password-sidebar-btn"
              onClick={() => setChangePasswordOpen(true)}
              className="flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#C5A059] transition-colors"
            >
              <Key size={16} />
              <span>Password</span>
            </button>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#9E2A2B] transition-colors"
            >
              <LogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-[260px] flex-1 min-h-screen">
        <div className="p-8">
          <Outlet />
        </div>
      </main>

      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </div>
  );
}
